import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button, Empty, Input, Modal, Money, Notice, Panel, Select, Table, type ColumnDef,
} from '../design/index.js'
import {
  useApi,
  type ItemOverviewRow,
  type PriceHistoryRow,
  type RateComparisonRow,
  type QuoteComparisonRow,
} from '../app/api.js'
import { Link } from '../app/Link.js'
import type { StatementSupplier } from '../app/supply-chain-types.js'
import { paths } from '../app/router.js'
import { ErrorBlock, Loading } from './feedback.js'
import { formatDate, formatQuantity, formatRate } from './format.js'

/**
 * The three modals a buyer opens from a purchase order (docs/06 §4).
 *
 * They are modals rather than pages because each is asked WITH the order in
 * front of you — "is this rate reasonable" is a question about the line under
 * the cursor, and sending somebody to a report loses which line they meant.
 */

export type InsightKind = 'items' | 'rates' | 'history' | 'quotes'

export interface OrderInsightsProps {
  readonly docKey: string
  readonly documentId: string
  readonly kind: InsightKind
  readonly onClose: () => void
}

export const OrderInsights = (props: OrderInsightsProps) =>
  props.kind === 'items' ? <ItemsOverview {...props} />
    : props.kind === 'rates' ? <RateComparison {...props} />
      : props.kind === 'quotes' ? <QuoteComparison {...props} />
        : <PriceHistory {...props} />

/* ---- quotations, side by side -------------------------------------------- */

/**
 * The comparative statement (docs/24 §3.5).
 *
 * One row per supplier with what they would cost landed on site — basic,
 * charges, tax — how quickly and on what terms, how they have performed, and
 * a single score that weighs price against performance by the share the
 * company set. Below it, every RFQ line with each supplier's rate and the L1
 * marked, because the cheapest total is rarely the cheapest on every line and
 * the buyer may split the award (docs/video-notes/06 §5).
 */
const QuoteComparison = ({ docKey, documentId, onClose }: OrderInsightsProps) => {
  const api = useApi()
  const queryClient = useQueryClient()
  const key = ['comparative-statement', api.companyId, documentId]
  const data = useQuery({ queryKey: key, queryFn: () => api.comparativeStatement(documentId) })
  const [split, setSplit] = useState(false)
  const award = useMutation({
    mutationFn: (quotationId: string) => api.awardQuotation(documentId, quotationId, !split),
    onSuccess: (next) => {
      queryClient.setQueryData(key, next)
      void queryClient.invalidateQueries({ queryKey: ['doc', api.companyId, docKey, documentId] })
    },
  })

  const statement = data.data
  const suppliers = statement?.suppliers ?? []

  const summary: ReadonlyArray<ColumnDef<StatementSupplier>> = [
    { key: 'rank', header: 'Rank', width: 70,
      render: (row) => (row.rank === null ? <span className="muted">Partial</span> : <strong>L{row.rank}</strong>) },
    { key: 'supplierName', header: 'Supplier', width: 230,
      render: (row) => (
        <>
          <div>{row.supplierName}{row.recommended ? ' ★' : ''}</div>
          <div className="cell-sub">
            {row.quotationNo ?? 'Draft'} · {row.linesQuoted} of {row.linesAsked} lines
            {row.vendor.approvalStatus && row.vendor.approvalStatus !== 'APPROVED'
              ? ` · ${row.vendor.approvalStatus.toLowerCase()}` : ''}
          </div>
        </>
      ) },
    { key: 'basic', header: 'Basic', width: 130, align: 'right', render: (row) => <Money value={row.basic} /> },
    { key: 'charges', header: 'Charges', width: 110, align: 'right', render: (row) => <Money value={row.charges} /> },
    { key: 'tax', header: 'Tax', width: 110, align: 'right', render: (row) => <Money value={row.tax} /> },
    { key: 'landed', header: 'Landed', width: 140, align: 'right',
      render: (row) => <strong><Money value={row.landed} /></strong> },
    { key: 'leadTime', header: 'Lead time', width: 120,
      render: (row) => (row.leadTime ?? '—') },
    { key: 'terms', header: 'Terms', width: 160,
      render: (row) => (
        <>
          <div>{row.paymentTerms ?? '—'}</div>
          {/* The credit period, unless the terms already say it. */}
          {row.creditPeriodDays !== null && !(row.paymentTerms ?? '').toLowerCase().includes('credit')
            ? <div className="cell-sub">{row.creditPeriodDays > 0 ? `${row.creditPeriodDays} days credit` : 'No credit'}</div>
            : null}
        </>
      ) },
    { key: 'vendor', header: 'Vendor', width: 120, align: 'right',
      render: (row) => row.vendorBasis === 'EVALUATION'
        ? <span className="num">{Number(row.vendorScoreUsed).toFixed(0)}{row.vendor.grade ? ` · ${row.vendor.grade}` : ''}</span>
        : row.vendorBasis === 'MEASURED'
          ? <span className="num" title="No evaluation yet: what the last year's receipts, inspections and quotations measure">{Number(row.vendorScoreUsed).toFixed(0)} · measured</span>
          : <span className="muted" title="No evaluation and no record: scored neutral">Not rated · 50</span> },
    { key: 'priceScore', header: 'Price', width: 80, align: 'right',
      render: (row) => <span className="num">{Number(row.priceScore).toFixed(0)}</span> },
    { key: 'combinedScore', header: 'Score', width: 80, align: 'right',
      render: (row) => <strong className="num">{Number(row.combinedScore).toFixed(1)}</strong> },
    { key: 'decision', header: 'Award', width: 150,
      render: (row) => row.decision === 'AWARDED' ? <strong>Awarded</strong>
        : row.decision === 'REJECTED' ? <span className="muted">Rejected</span>
          : row.quotationStatus !== 'APPROVED' ? <span className="muted">Approve it first</span>
            : (
              <Button kind={row.recommended ? 'primary' : 'ghost'} disabled={award.isPending}
                onClick={() => award.mutate(row.quotationId)}>
                Award
              </Button>
            ) },
  ]

  const lines: ReadonlyArray<ColumnDef<QuoteComparisonRow>> = [
    { key: 'item', header: 'Material', width: 320,
      render: (row) => (
        <>
          <div>{row.item}</div>
          {row.description ? <div className="cell-sub">{row.description}</div> : null}
        </>
      ) },
    { key: 'quantity', header: 'Qty', width: 100, align: 'right',
      render: (row) => <span className="num">{row.quantity ? formatQuantity(row.quantity) : '—'}</span> },
    { key: 'estimatedRate', header: 'Estimated', width: 120, align: 'right',
      render: (row) => <span className="num">{row.estimatedRate ? formatRate(row.estimatedRate) : '—'}</span> },
    ...suppliers.map((sup) => ({
      key: `sup-${sup.supplierId}`,
      header: sup.supplierName,
      width: 150,
      align: 'right' as const,
      render: (row: QuoteComparisonRow) => {
        const quote = row.quotes.find((q: QuoteComparisonRow['quotes'][number]) => q.supplierId === sup.supplierId)
        if (!quote || quote.rate === null) return <span className="num">—</span>
        return (
          <span className="num" style={quote.lowest ? { fontWeight: 600 } : undefined}>
            {formatRate(quote.rate)}
            {quote.lowest ? ' · L1' : ''}
          </span>
        )
      },
    })),
  ]

  const awarded = suppliers.find((s) => s.decision === 'AWARDED')

  return (
    <Modal
      title={`Comparative statement${statement?.rfqNo ? ` — ${statement.rfqNo}` : ''}`}
      wide
      onClose={onClose}
      actions={
        <>
          {awarded ? (
            <Link
              to={paths.create(api.companyId, 'pur_ords', { from: awarded.quotationId, fromType: 'supplier_quotation' })}
              className="btn btn--primary"
            >
              Raise the purchase order
            </Link>
          ) : null}
          <Button kind="ghost" onClick={onClose}>Close</Button>
        </>
      }
      footerLeft={
        statement
          ? `${suppliers.length} supplier${suppliers.length === 1 ? '' : 's'} answered · price is ${statement.priceWeightPct}% of the score`
          : undefined
      }
    >
      {data.isLoading ? <Loading /> : data.error ? <ErrorBlock error={data.error} /> : (
        (statement?.rows.length ?? 0) === 0 || suppliers.length === 0 ? (
          <Empty
            title="Nothing to compare yet"
            body={['Quotations appear here as suppliers answer — through the portal, or keyed in by hand with the RFQ named on them.']}
          />
        ) : (
          <>
            {statement?.recommendation ? (
              <Notice kind="ok">
                <strong>Recommended: {statement.recommendation.supplierName}.</strong>{' '}
                {statement.recommendation.reason}.
              </Notice>
            ) : null}
            {award.error ? <ErrorBlock error={award.error} /> : null}
            <Table columns={summary} rows={suppliers} getKey={(row) => row.quotationId} />
            <label className="row" style={{ margin: '10px 0 18px', gap: 8 }}>
              <input type="checkbox" checked={split} onChange={(e) => setSplit(e.currentTarget.checked)} />
              <span>Split the award — awarding one does not reject the others</span>
            </label>
            <p className="muted" style={{ marginBottom: 14 }}>
              The lowest rate on each line is marked L1. Every line at its L1 would total{' '}
              <Money value={statement?.lowestSplit ?? '0'} /> before charges and tax.
            </p>
            <Table columns={lines} rows={statement?.rows ?? []} getKey={(row) => row.lineId} />
          </>
        )
      )}
    </Modal>
  )
}

/* ---- items overview ------------------------------------------------------ */

const ItemsOverview = ({ docKey, documentId, onClose }: OrderInsightsProps) => {
  const api = useApi()
  const [q, setQ] = useState('')
  const rows = useQuery({
    queryKey: ['insight-items', api.companyId, docKey, documentId],
    queryFn: () => api.itemsOverview(docKey, documentId),
  })

  const columns: ReadonlyArray<ColumnDef<ItemOverviewRow>> = [
    { key: 'seq', header: '#', width: 60, align: 'right' },
    {
      key: 'item',
      header: 'Material',
      render: (r) => (
        <>
          {r.item}
          {r.description ? <div className="helper">{r.description}</div> : null}
        </>
      ),
    },
    { key: 'billable', header: 'Type', width: 140 },
    { key: 'boqRef', header: 'BOQ ref', width: 110, render: (r) => r.boqRef ?? '—' },
    // The column that catches a line bought on behalf of another job.
    { key: 'forWhom', header: 'For', width: 200 },
    { key: 'projectName', header: 'Project', width: 240, render: (r) => r.projectName ?? '—' },
    {
      key: 'quantity',
      header: 'Quantity',
      align: 'right',
      width: 130,
      render: (r) => `${formatQuantity(r.quantity)} ${r.unit ?? ''}`.trim(),
    },
    /*
      What is already on the shelf.

      The vendor names stock as the first of the three checks before an order
      is approved — "check stock before order approval" — and the figure lived
      only inside the line picker, at creation time, so the approver the
      control is for could never see it. A line already covered by stock is
      called out rather than left to be read off a number.
    */
    {
      key: 'stockOnHand',
      header: 'In stock',
      align: 'right',
      width: 130,
      render: (r) => {
        if (r.stockOnHand === null) return '—'
        const covered = Number(r.stockOnHand) > 0 && Number(r.stockOnHand) >= Number(r.quantity ?? 0)
        return (
          <>
            {formatQuantity(r.stockOnHand)}
            {covered ? <div className="helper">already covered</div> : null}
          </>
        )
      },
    },
    { key: 'rate', header: 'Rate', align: 'right', width: 120, render: (r) => formatRate(r.rate) },
    {
      key: 'discountPct',
      header: 'Discount',
      align: 'right',
      width: 110,
      render: (r) => (Number(r.discountPct) === 0 ? '—' : `${Number(r.discountPct)}%`),
    },
    { key: 'amount', header: 'Amount', type: 'money', align: 'right', width: 150 },
  ]

  const filtered = (rows.data ?? []).filter((r) =>
    q.trim() === '' || `${r.item} ${r.billable} ${r.boqRef ?? ''}`.toLowerCase().includes(q.trim().toLowerCase()))

  return (
    <Modal
      title="Items overview"
      subtitle="Everything on this order, what is already in stock, and which job each line is for."
      wide
      footerLeft={`${filtered.length} line${filtered.length === 1 ? '' : 's'}`}
      actions={<Button kind="ghost" onClick={onClose}>Close</Button>}
      onClose={onClose}
    >
      <div style={{ marginBottom: 12, maxWidth: 380 }}>
        <Input value={q} onChange={setQ} placeholder="Search by item, type or BOQ ref" />
      </div>
      {rows.isLoading ? <Loading /> : null}
      {rows.error ? <ErrorBlock error={rows.error} /> : null}
      <Table
        columns={columns}
        rows={filtered}
        getKey={(r) => r.lineId}
        empty={<Empty title="No lines" body={['This order has no material lines on it yet.']} />}
      />
    </Modal>
  )
}

/* ---- ordered against estimated ------------------------------------------- */

const RateComparison = ({ docKey, documentId, onClose }: OrderInsightsProps) => {
  const api = useApi()
  const [q, setQ] = useState('')
  const [showBoq, setShowBoq] = useState(false)
  const rows = useQuery({
    queryKey: ['insight-rates', api.companyId, docKey, documentId],
    queryFn: () => api.rateComparison(docKey, documentId),
  })

  const columns: ReadonlyArray<ColumnDef<RateComparisonRow>> = [
    {
      key: 'item',
      header: 'Material',
      render: (r) => (
        <>
          {r.item}
          {r.description ? <div className="helper">{r.description}</div> : null}
        </>
      ),
    },
    { key: 'brand', header: 'Brand', width: 140, render: (r) => r.brand ?? '—' },
    {
      key: 'quantity',
      header: 'Quantity',
      align: 'right',
      width: 130,
      render: (r) => `${formatQuantity(r.quantity)} ${r.unit ?? ''}`.trim(),
    },
    {
      key: 'estimatedRate',
      header: 'Estimated rate',
      align: 'right',
      width: 150,
      render: (r) => (r.estimatedRate === null ? '—' : formatRate(r.estimatedRate)),
    },
    ...(showBoq
      ? [{
        key: 'boqRate',
        header: 'BOQ rate',
        align: 'right' as const,
        width: 140,
        render: (r: RateComparisonRow) => (r.boqRate === null ? '—' : formatRate(r.boqRate)),
      }]
      : []),
    {
      key: 'orderedRate',
      header: 'Order rate',
      align: 'right',
      width: 140,
      render: (r) => (r.orderedRate === null ? '—' : formatRate(r.orderedRate)),
    },
    {
      key: 'quotedRate',
      header: 'Quote rate',
      align: 'right',
      width: 140,
      render: (r) => (r.quotedRate === null ? '—' : formatRate(r.quotedRate)),
    },
    {
      key: 'differenceAmount',
      header: 'Difference',
      align: 'right',
      width: 160,
      render: (r) => (r.differenceAmount === null
        ? '—'
        // Negative means the order is above the estimate. That is the number
        // the whole modal exists to show, so it is not hidden behind a sign.
        : <span className={Number(r.differenceAmount) < 0 ? 'over' : undefined}>
          <Money value={r.differenceAmount} />
        </span>),
    },
    {
      key: 'differencePct',
      header: '%',
      align: 'right',
      width: 90,
      render: (r) => (r.differencePct === null ? '—' : `${r.differencePct}%`),
    },
  ]

  const filtered = (rows.data ?? []).filter((r) =>
    q.trim() === '' || `${r.item} ${r.description ?? ''}`.toLowerCase().includes(q.trim().toLowerCase()))
  const over = filtered.filter((r) => r.differenceAmount !== null && Number(r.differenceAmount) < 0).length

  return (
    <Modal
      title="Order rate against estimated rate"
      subtitle="Where this order sits against what the job was costed at."
      wide
      footerLeft={over > 0 ? `${over} line${over === 1 ? '' : 's'} above the estimate` : 'Nothing above the estimate'}
      actions={<Button kind="ghost" onClick={onClose}>Close</Button>}
      onClose={onClose}
    >
      <div className="row row--wrap" style={{ marginBottom: 12 }}>
        <div style={{ maxWidth: 380, flex: 1 }}>
          <Input value={q} onChange={setQ} placeholder="Search by material or description" />
        </div>
        <label className="column-choice">
          <input type="checkbox" checked={showBoq} onChange={() => setShowBoq((on) => !on)} />
          <span>Show the BOQ rate</span>
        </label>
      </div>
      {rows.isLoading ? <Loading /> : null}
      {rows.error ? <ErrorBlock error={rows.error} /> : null}
      <Table
        columns={columns}
        rows={filtered}
        getKey={(r) => r.lineId}
        empty={<Empty title="Nothing to compare" body={['This order has no lines raised against an estimate.']} />}
      />
    </Modal>
  )
}

/* ---- price history ------------------------------------------------------- */

const SOURCE_TABS: ReadonlyArray<{ key: PriceHistoryRow['source']; label: string }> = [
  { key: 'PURCHASE', label: 'Purchase' },
  { key: 'RATE_CONTRACT', label: 'Rate contract' },
  { key: 'SUPPLIER_QUOTE', label: 'Supplier quote' },
  { key: 'SUBCONTRACT', label: 'Subcontract' },
]

const PriceHistory = ({ docKey, documentId, onClose }: OrderInsightsProps) => {
  const api = useApi()
  const lines = useQuery({
    queryKey: ['insight-items', api.companyId, docKey, documentId],
    queryFn: () => api.itemsOverview(docKey, documentId),
  })

  const [index, setIndex] = useState(0)
  const [source, setSource] = useState<PriceHistoryRow['source']>('PURCHASE')
  const [thisSupplierOnly, setThisSupplierOnly] = useState(true)
  const [brand, setBrand] = useState('')

  const line = lines.data?.[index]
  const history = useQuery({
    queryKey: ['insight-history', api.companyId, docKey, documentId, line?.lineId, thisSupplierOnly, brand],
    queryFn: () => api.priceHistory(docKey, documentId, line?.lineId ?? '', {
      thisSupplierOnly,
      ...(brand ? { brand } : {}),
    }),
    enabled: Boolean(line?.lineId),
  })

  const columns: ReadonlyArray<ColumnDef<PriceHistoryRow>> = [
    { key: 'supplierName', header: 'Supplier', render: (r) => r.supplierName ?? '—' },
    {
      key: 'documentNo',
      header: 'Document',
      width: 190,
      render: (r) => (
        <Link to={paths.document(api.companyId, r.route, r.documentId)} className="mono">
          {r.documentNo ?? '—'}
        </Link>
      ),
    },
    { key: 'documentDate', header: 'Date', width: 130, render: (r) => formatDate(r.documentDate) },
    { key: 'brand', header: 'Brand', width: 130, render: (r) => r.brand ?? '—' },
    {
      key: 'quantity',
      header: 'Qty',
      align: 'right',
      width: 120,
      render: (r) => `${formatQuantity(r.quantity)} ${r.unit ?? ''}`.trim(),
    },
    { key: 'rate', header: 'Rate', align: 'right', width: 120, render: (r) => formatRate(r.rate) },
    {
      key: 'discountPct',
      header: 'Discount',
      align: 'right',
      width: 110,
      render: (r) => (Number(r.discountPct) === 0 ? '—' : `${Number(r.discountPct)}%`),
    },
    { key: 'finalRate', header: 'Final rate', align: 'right', width: 130, render: (r) => formatRate(r.finalRate) },
    { key: 'paymentTerm', header: 'Payment term', width: 150, render: (r) => r.paymentTerm ?? '—' },
  ]

  const rows = (history.data?.rows ?? []).filter((r) => r.source === source)
  const count = lines.data?.length ?? 0

  return (
    <Modal
      title="Purchase price history"
      subtitle={line ? line.item : 'What this material has cost before.'}
      wide
      footerLeft={count > 0 ? `Line ${index + 1} of ${count}` : ''}
      actions={
        <>
          <Button kind="ghost" disabled={index === 0} onClick={() => setIndex((i) => i - 1)}>Previous</Button>
          <Button kind="ghost" disabled={index >= count - 1} onClick={() => setIndex((i) => i + 1)}>Next</Button>
          <Button kind="ghost" onClick={onClose}>Close</Button>
        </>
      }
      onClose={onClose}
    >
      {line ? (
        <Panel title="On this order">
          <div className="row row--wrap" style={{ gap: 32 }}>
            <div><div className="label">Quantity</div><div>{formatQuantity(line.quantity)} {line.unit}</div></div>
            <div><div className="label">Rate</div><div>{formatRate(line.rate)}</div></div>
            <div>
              <div className="label">Discount</div>
              <div>{Number(line.discountPct) === 0 ? '—' : `${Number(line.discountPct)}%`}</div>
            </div>
            <div><div className="label">Final rate</div><div>{formatRate(history.data?.finalRate ?? line.rate)}</div></div>
            <div><div className="label">Brand</div><div>{history.data?.brand ?? '—'}</div></div>
          </div>
        </Panel>
      ) : null}

      <nav className="tabstrip" aria-label="Where the price came from">
        {SOURCE_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={tab.key === source ? 'tabstrip__tab tabstrip__tab--active' : 'tabstrip__tab'}
            aria-current={tab.key === source ? 'page' : undefined}
            onClick={() => setSource(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="row row--wrap" style={{ margin: '12px 0' }}>
        <Button kind="ghost" onClick={() => setThisSupplierOnly(true)}>
          {thisSupplierOnly ? '● This supplier' : 'This supplier'}
        </Button>
        <Button kind="ghost" onClick={() => setThisSupplierOnly(false)}>
          {thisSupplierOnly ? 'All suppliers' : '● All suppliers'}
        </Button>
        <div style={{ minWidth: 200 }}>
          <Select
            value={brand}
            onChange={setBrand}
            placeholder="Any brand"
            options={(history.data?.brands ?? []).map((b) => ({ value: b, label: b }))}
          />
        </div>
        <Link to={paths.report(api.companyId, 'purchase_price_history')}>
          Take me to the Purchase price records report →
        </Link>
      </div>

      {history.isLoading ? <Loading /> : null}
      {history.error ? <ErrorBlock error={history.error} /> : null}
      <Table
        columns={columns}
        rows={rows}
        getKey={(r) => `${r.documentId}:${r.documentDate}:${r.rate ?? ''}`}
        empty={
          <Empty
            title="Nothing bought before"
            body={[
              thisSupplierOnly
                ? 'This supplier has not been asked for this material before. Try all suppliers.'
                : 'This material has not been bought under this kind of document before.',
            ]}
          />
        }
      />
    </Modal>
  )
}
